import { MenuOptionType } from "@dexteel/mesf-core";
import { TableView, Comment, CallSplit } from "@mui/icons-material";
import { type QueryKey, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { SplitDelaysModal } from "@/pages/delays/manager/components/modals/SplitDelaysModal";
import { UpsertJustifyDelayModal } from "@/pages/delays/manager/components/modals/UpsertJustifyDelayModal";
import { routeDelaysManager } from "@/setup/routes/delay.routes";

export const GANTT_DELAYS_MENU_ID = "ganttDelaysMenu";

interface DelayData {
	DelayId: number;
	ShiftId: number;
	AssetId?: number;
}

export const useGanttOptionsContextMenu = (
	queryKey: QueryKey,
	suffixTitle: string,
) => {
	const queryClient = useQueryClient();
	const [showJustifyModal, setShowJustifyModal] = useState(false);
	const [showSplitModal, setShowSplitModal] = useState(false);
	const [selectedDelayId, setSelectedDelayId] = useState<number | null>(null);
	const [selectedAssetId, setSelectedAssetId] = useState<number | null>(null);
	const navigate = useNavigate();

	const goToDelaysManager = (data: DelayData) => {
		navigate(
			`${routeDelaysManager}/${data.ShiftId}?assetId=${data.AssetId}&delayId=${data.DelayId}`,
		);
	};

	const justifyData = (data: DelayData) => {
		setSelectedDelayId(data.DelayId);
		setSelectedAssetId(data.AssetId ?? null);
		setShowJustifyModal(true);
	};

	const splitData = (data: DelayData) => {
		setSelectedDelayId(data.DelayId);
		setSelectedAssetId(data.AssetId ?? null);
		setShowSplitModal(true);
	};

	const getOptions = (data: DelayData): MenuOptionType[] => {
		return [
			{
				name: "Justify Delay",
				key: "justify_delay",
				onClick: () => justifyData(data),
				icon: <Comment />,
			},
			{
				name: "Split Delay",
				key: "split_delay",
				onClick: () => splitData(data),
				icon: <CallSplit />,
			},
			{
				name: "See in Manager",
				key: "delays_manager",
				onClick: () => goToDelaysManager(data),
				icon: <TableView />,
			},
		];
	};

	const handleHide = (shouldUpdate: boolean) => {
		setShowJustifyModal(false);
		setShowSplitModal(false);
		if (shouldUpdate) {
			queryClient.invalidateQueries({ queryKey });
		}
	};

	const menu = (
		<>
			<UpsertJustifyDelayModal
				showModal={showJustifyModal ? "justify" : ""}
				onHide={handleHide}
				delayId={selectedDelayId}
				assetId={selectedAssetId ?? undefined}
				suffixTitle={suffixTitle}
			/>
			<SplitDelaysModal
				show={showSplitModal}
				onHide={handleHide}
				delayId={selectedDelayId}
				assetId={selectedAssetId ?? undefined}
				suffixTitle={suffixTitle}
			/>
		</>
	);

	return { getOptions, menu };
};
